import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession as amplifyFetchAuthSession,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  updatePassword as amplifyUpdatePassword,
} from 'aws-amplify/auth';

import { getConfig } from '@/config/runtimeConfig';

// Defer Amplify configuration to first use (GW-6402: avoid getConfig() at module load)
let amplifyConfigured = false;

/**
 * GW-6829: Fetch the caller's IP address from the /public/ip endpoint.
 * Fail-open: returns undefined if the fetch fails or exceeds 500ms.
 * The IP is passed as clientMetadata.sourceIP in the Cognito signIn call
 * so the post-authentication Lambda can write last_login_ip to gw-users.
 */
async function fetchClientIP(): Promise<string | undefined> {
  const { apiBaseUrl } = getConfig();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 500);

  try {
    const response = await fetch(`${apiBaseUrl}/public/ip`, {
      signal: controller.signal,
    });
    if (!response.ok) return undefined;
    const data = (await response.json()) as { ip?: string };
    return data.ip || undefined;
  } catch {
    return undefined;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Configures Amplify's Cognito Auth block, once, on first use (deferred to
 * avoid `getConfig()` at module load — GW-6402). Exported (GW-8887) so the
 * notifications connection manager can guarantee the Cognito block is present
 * before it spread-composes the AppSync Events transport config on top of it;
 * both callers share the single `amplifyConfigured` latch so Auth is
 * configured exactly once regardless of which runs first.
 */
export function ensureAmplifyConfigured(): void {
  if (amplifyConfigured) return;

  const config = getConfig();
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.cognito.userPoolId,
        userPoolClientId: config.cognito.clientId,
      },
    },
  });
  amplifyConfigured = true;
}

/**
 * Direct authentication with cognito-local for dev-stack mode.
 * Amplify v6 doesn't support custom endpoints, so we bypass it entirely.
 */
async function devStackSignIn(
  username: string,
  password: string
): Promise<{
  success: boolean;
  isSignedIn?: boolean;
  tokens?: { idToken: string; accessToken: string; refreshToken: string };
  error?: unknown;
}> {
  const { cognito } = getConfig();
  const cognitoEndpoint = cognito.endpoint;
  const userPoolClientId = cognito.clientId;

  try {
    const response = await fetch(cognitoEndpoint!, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: userPoolClientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      }),
    });

    const data = await response.json();

    if (data.AuthenticationResult) {
      // Store tokens in localStorage for dev-stack session management
      const tokens = {
        idToken: data.AuthenticationResult.IdToken,
        accessToken: data.AuthenticationResult.AccessToken,
        refreshToken: data.AuthenticationResult.RefreshToken || '',
      };
      localStorage.setItem('devstack-auth-tokens', JSON.stringify(tokens));
      localStorage.setItem('devstack-auth-username', username);
      return { success: true, isSignedIn: true, tokens };
    }

    // Handle error response
    const errorMessage = data.message || data.__type || 'Authentication failed';
    console.error('❌ Dev-stack sign in error:', errorMessage);
    return { success: false, error: new Error(errorMessage) };
  } catch (error) {
    console.error('❌ Dev-stack sign in network error:', error);
    return { success: false, error };
  }
}

/**
 * GW-749: Parse Cognito signUp error codes for user-friendly messages and recovery options
 */
function parseSignUpError(errorName: string): {
  errorCode: string;
  userMessage: string;
  canResendVerification: boolean;
} {
  switch (errorName) {
    case 'UsernameExistsException':
      return {
        errorCode: 'UsernameExistsException',
        userMessage:
          "An account with this email already exists. If you haven't verified your email, you can request a new verification code.",
        canResendVerification: true,
      };
    case 'InvalidPasswordException':
      return {
        errorCode: 'InvalidPasswordException',
        userMessage:
          'Password does not meet requirements. Please use at least 12 characters with uppercase, lowercase, numbers, and special characters.',
        canResendVerification: false,
      };
    case 'InvalidParameterException':
      return {
        errorCode: 'InvalidParameterException',
        userMessage: 'Invalid email format. Please enter a valid email address.',
        canResendVerification: false,
      };
    default:
      return {
        errorCode: 'UnknownError',
        userMessage: 'Sign up failed. Please try again.',
        canResendVerification: false,
      };
  }
}

/**
 * GW-1294: Parse Cognito confirmSignUp error codes for user-friendly messages and recovery options
 */
function parseConfirmSignUpError(errorName: string): {
  errorCode: string;
  userMessage: string;
  canResendCode: boolean;
  isAlreadyConfirmed?: boolean;
} {
  switch (errorName) {
    case 'ExpiredCodeException':
      return {
        errorCode: 'ExpiredCodeException',
        userMessage: 'Verification code has expired. Please request a new code.',
        canResendCode: true,
      };
    case 'CodeMismatchException':
      return {
        errorCode: 'CodeMismatchException',
        userMessage: 'Invalid verification code. Please check the code and try again.',
        canResendCode: false,
      };
    case 'NotAuthorizedException':
      return {
        errorCode: 'NotAuthorizedException',
        userMessage: 'Your account has already been verified. Please sign in.',
        canResendCode: false,
        isAlreadyConfirmed: true,
      };
    case 'LimitExceededException':
      return {
        errorCode: 'LimitExceededException',
        userMessage: 'Too many attempts. Please wait a few minutes before trying again.',
        canResendCode: false,
      };
    default:
      return {
        errorCode: 'UnknownError',
        userMessage: 'Verification failed. Please try again.',
        canResendCode: false,
      };
  }
}

/**
 * GW-807: Direct forgot password with cognito-local for dev-stack mode.
 */
async function devStackForgotPassword(username: string): Promise<{
  success: boolean;
  error?: unknown;
  userMessage?: string;
}> {
  const { cognito } = getConfig();
  const cognitoEndpoint = cognito.endpoint;
  const userPoolClientId = cognito.clientId;

  try {
    const response = await fetch(cognitoEndpoint!, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword',
      },
      body: JSON.stringify({
        ClientId: userPoolClientId,
        Username: username,
      }),
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true };
    }

    const errorMessage = data.message || data.__type || 'Failed to send reset code';
    console.error('❌ Dev-stack forgot password error:', errorMessage);
    return { success: false, error: new Error(errorMessage), userMessage: errorMessage };
  } catch (error) {
    console.error('❌ Dev-stack forgot password network error:', error);
    return { success: false, error, userMessage: 'Network error. Please try again.' };
  }
}

/**
 * GW-807: Direct confirm forgot password with cognito-local for dev-stack mode.
 */
async function devStackConfirmForgotPassword(
  username: string,
  confirmationCode: string,
  newPassword: string
): Promise<{
  success: boolean;
  error?: unknown;
  userMessage?: string;
}> {
  const { cognito } = getConfig();
  const cognitoEndpoint = cognito.endpoint;
  const userPoolClientId = cognito.clientId;

  try {
    const response = await fetch(cognitoEndpoint!, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
      },
      body: JSON.stringify({
        ClientId: userPoolClientId,
        Username: username,
        ConfirmationCode: confirmationCode,
        Password: newPassword,
      }),
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true };
    }

    const errorMessage = data.message || data.__type || 'Failed to reset password';
    console.error('❌ Dev-stack confirm forgot password error:', errorMessage);
    return { success: false, error: new Error(errorMessage), userMessage: errorMessage };
  } catch (error) {
    console.error('❌ Dev-stack confirm forgot password network error:', error);
    return { success: false, error, userMessage: 'Network error. Please try again.' };
  }
}

/**
 * GW-807: Parse Cognito forgotPassword error codes for user-friendly messages
 */
function parseForgotPasswordError(error: unknown): string {
  const err = error as { name?: string; message?: string };
  switch (err.name) {
    case 'UserNotFoundException':
      // Security: Don't reveal if user exists
      return 'If an account exists with this email, you will receive a reset code.';
    case 'LimitExceededException':
      return 'Too many attempts. Please try again later.';
    case 'InvalidParameterException':
      return 'Please enter a valid email address.';
    case 'NotAuthorizedException':
      return 'Unable to reset password for this account. Please contact support.';
    default:
      return 'Unable to process your request. Please try again.';
  }
}

/**
 * GW-807: Parse Cognito confirmResetPassword error codes for user-friendly messages
 */
function parseResetPasswordError(error: unknown): string {
  const err = error as { name?: string; message?: string };
  switch (err.name) {
    case 'CodeMismatchException':
      return 'Invalid verification code. Please check and try again.';
    case 'ExpiredCodeException':
      return 'Verification code has expired. Please request a new one.';
    case 'InvalidPasswordException':
      return 'Password must be at least 12 characters with uppercase, lowercase, numbers, and special characters.';
    case 'LimitExceededException':
      return 'Too many attempts. Please try again later.';
    case 'UserNotFoundException':
      return 'Unable to reset password. Please request a new reset code.';
    default:
      return 'Unable to reset password. Please try again.';
  }
}

// Authentication helper functions
export const auth = {
  /**
   * Sign in a user
   * Uses direct cognito-local calls in dev-stack mode (Amplify v6 doesn't support custom endpoints)
   */
  async signIn(username: string, password: string) {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    // Use dev-stack direct auth if cognito-local endpoint is configured
    if (isDevStackMode) {
      return devStackSignIn(username, password);
    }

    // Production mode: use Amplify
    ensureAmplifyConfigured();
    try {
      // GW-6829: Best-effort IP fetch for post-authentication Lambda (last_login_ip).
      // Fail-open: sign-in proceeds even if IP fetch times out or fails.
      const sourceIP = await fetchClientIP();
      const signInOptions = sourceIP ? { options: { clientMetadata: { sourceIP } } } : {};
      const { isSignedIn, nextStep } = await amplifySignIn({
        username,
        password,
        ...signInOptions,
      });
      return { success: true, isSignedIn, nextStep };
    } catch (error) {
      console.error('❌ Sign in error:', error);
      console.error('Error details:', {
        name: (error as Error).name,
        message: (error as Error).message,
      });
      return { success: false, error };
    }
  },

  /**
   * Sign out the current user
   */
  async signOut() {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    // Clear dev-stack tokens if present
    if (isDevStackMode) {
      localStorage.removeItem('devstack-auth-tokens');
      localStorage.removeItem('devstack-auth-username');
      return { success: true };
    }

    ensureAmplifyConfigured();
    try {
      await amplifySignOut();
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      return { success: false, error };
    }
  },

  /**
   * Sign up a new user
   * GW-749: Parse Cognito error codes for specific user messages and recovery options
   */
  async signUp(username: string, password: string, email: string) {
    ensureAmplifyConfigured();
    try {
      const { isSignUpComplete, userId, nextStep } = await amplifySignUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      return { success: true, isSignUpComplete, userId, nextStep };
    } catch (error) {
      console.error('Sign up error:', error);

      // GW-749: Parse Cognito error codes for specific messages and recovery options
      const errorName = (error as Error & { name?: string }).name || 'UnknownError';
      const { errorCode, userMessage, canResendVerification } = parseSignUpError(errorName);

      return {
        success: false,
        error,
        errorCode,
        userMessage,
        canResendVerification,
      };
    }
  },

  /**
   * Confirm sign up with verification code
   * GW-1294: Parse Cognito error codes for specific user messages and recovery options
   */
  async confirmSignUp(username: string, confirmationCode: string) {
    ensureAmplifyConfigured();
    try {
      const { isSignUpComplete, nextStep } = await amplifyConfirmSignUp({
        username,
        confirmationCode,
      });
      return { success: true, isSignUpComplete, nextStep };
    } catch (error) {
      console.error('Confirm sign up error:', error);

      // GW-1294: Parse Cognito error codes for specific messages and recovery options
      const errorName = (error as Error & { name?: string }).name || 'UnknownError';
      const { errorCode, userMessage, canResendCode, isAlreadyConfirmed } =
        parseConfirmSignUpError(errorName);

      return {
        success: false,
        error,
        errorCode,
        userMessage,
        canResendCode,
        isAlreadyConfirmed,
      };
    }
  },

  /**
   * Resend sign up confirmation code
   * GW-805: Detect already-confirmed users and return isAlreadyConfirmed flag
   */
  async resendSignUpCode(username: string) {
    ensureAmplifyConfigured();
    try {
      const response = await amplifyResendSignUpCode({
        username,
      });
      return { success: true, response };
    } catch (error) {
      console.error('Resend sign up code error:', error);

      // GW-805: Detect "already confirmed" error from Cognito
      // Cognito throws NotAuthorizedException when user is already CONFIRMED
      const errorName = (error as Error & { name?: string }).name;
      const errorMessage = (error as Error).message || '';

      if (
        errorName === 'NotAuthorizedException' &&
        errorMessage.toLowerCase().includes('confirmed')
      ) {
        return { success: false, isAlreadyConfirmed: true, error };
      }

      return { success: false, error };
    }
  },

  /**
   * Get the current authenticated user
   */
  async getCurrentUser() {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    // Check dev-stack auth
    if (isDevStackMode) {
      const username = localStorage.getItem('devstack-auth-username');
      if (username) {
        return { success: true, user: { username, userId: username } };
      }
      return { success: false, error: new Error('Not authenticated') };
    }

    ensureAmplifyConfigured();
    try {
      const user = await amplifyGetCurrentUser();
      return { success: true, user };
    } catch (error) {
      console.error('Get current user error:', error);
      return { success: false, error };
    }
  },

  /**
   * Get the current auth session with tokens
   */
  async getSession() {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    // Return dev-stack tokens if present
    if (isDevStackMode) {
      const tokensStr = localStorage.getItem('devstack-auth-tokens');
      if (tokensStr) {
        const tokens = JSON.parse(tokensStr);
        return {
          success: true,
          session: { tokens },
          tokens: {
            idToken: { toString: () => tokens.idToken },
            accessToken: { toString: () => tokens.accessToken },
          },
          credentials: null,
        };
      }
      return { success: false, error: new Error('No session') };
    }

    ensureAmplifyConfigured();
    try {
      const session = await amplifyFetchAuthSession();
      return {
        success: true,
        session,
        tokens: session.tokens,
        credentials: session.credentials,
      };
    } catch (error) {
      console.error('Get session error:', error);
      return { success: false, error };
    }
  },

  /**
   * Force a new auth session from Cognito, triggering the pre-token-generation
   * Lambda so updated profile claims are embedded in the refreshed JWT.
   */
  async refreshSession() {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    if (isDevStackMode) {
      return this.getSession();
    }

    ensureAmplifyConfigured();
    try {
      const session = await amplifyFetchAuthSession({ forceRefresh: true });
      return {
        success: true,
        session,
        tokens: session.tokens,
        credentials: session.credentials,
      };
    } catch (error) {
      console.error('Refresh session error:', error);
      return { success: false, error };
    }
  },

  /**
   * Get the ID token for API authentication
   */
  async getIdToken() {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    // Return dev-stack token if present
    if (isDevStackMode) {
      const tokensStr = localStorage.getItem('devstack-auth-tokens');
      if (tokensStr) {
        const tokens = JSON.parse(tokensStr);
        return { success: true, idToken: tokens.idToken };
      }
      return { success: false, error: new Error('No token') };
    }

    ensureAmplifyConfigured();
    try {
      const session = await amplifyFetchAuthSession();
      return {
        success: true,
        idToken: session.tokens?.idToken?.toString(),
      };
    } catch (error) {
      console.error('Get ID token error:', error);
      return { success: false, error };
    }
  },

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    // Check dev-stack auth
    if (isDevStackMode) {
      return !!localStorage.getItem('devstack-auth-tokens');
    }

    ensureAmplifyConfigured();
    try {
      await amplifyGetCurrentUser();
      return true;
    } catch {
      return false;
    }
  },

  /**
   * GW-807: Initiate forgot password flow
   * Sends a verification code to the user's email
   */
  async forgotPassword(username: string) {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    // Use dev-stack direct auth if cognito-local endpoint is configured
    if (isDevStackMode) {
      return devStackForgotPassword(username);
    }

    // Production mode: use Amplify
    ensureAmplifyConfigured();
    try {
      await amplifyResetPassword({ username });
      return { success: true };
    } catch (error) {
      console.error('❌ Forgot password error:', error);
      const userMessage = parseForgotPasswordError(error);
      return { success: false, error, userMessage };
    }
  },

  /**
   * GW-807: Confirm forgot password with code and new password
   * Completes the password reset flow
   */
  async confirmForgotPassword(username: string, confirmationCode: string, newPassword: string) {
    const { cognito } = getConfig();
    const isDevStackMode = cognito.endpoint !== null && cognito.endpoint.includes('localhost');

    // Use dev-stack direct auth if cognito-local endpoint is configured
    if (isDevStackMode) {
      return devStackConfirmForgotPassword(username, confirmationCode, newPassword);
    }

    // Production mode: use Amplify
    ensureAmplifyConfigured();
    try {
      await amplifyConfirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
      return { success: true };
    } catch (error) {
      console.error('❌ Confirm forgot password error:', error);
      const userMessage = parseResetPasswordError(error);
      return { success: false, error, userMessage };
    }
  },

  /**
   * Change the current user's password
   * Requires the user to be signed in
   */
  async changePassword(oldPassword: string, newPassword: string) {
    ensureAmplifyConfigured();
    try {
      await amplifyUpdatePassword({ oldPassword, newPassword });
      return { success: true };
    } catch (error) {
      console.error('❌ Change password error:', error);
      const errorMessage = (error as { message?: string }).message || 'Failed to change password';
      return { success: false, error, errorMessage };
    }
  },
};
